import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { ArrowClockwise, CheckCircle, XCircle, WarningCircle } from "@phosphor-icons/react";

const LABELS = {
  mongo: { title: "MongoDB", desc: "Base de données (fiches, users, reviews)" },
  resend: { title: "Resend", desc: "Envoi d'emails transactionnels" },
  cloudinary: { title: "Cloudinary", desc: "Hébergement photos & logos" },
  openai: { title: "OpenAI", desc: "IA (descriptions, posts, Concierge)" },
  google_oauth: { title: "Google OAuth", desc: "Connexion Google côté client" },
  paypal: { title: "PayPal", desc: "Abonnements Premium & Premium IA" },
  scheduler: { title: "APScheduler", desc: "Tâches planifiées (import, outreach, reports)" },
};

const ORDER = ["mongo", "cloudinary", "resend", "openai", "google_oauth", "paypal", "scheduler"];

function Dot({ ok, configured }) {
  if (!configured) return <WarningCircle size={22} weight="duotone" className="text-amber-500 shrink-0" />;
  return ok
    ? <CheckCircle size={22} weight="duotone" className="text-primary shrink-0" />
    : <XCircle size={22} weight="duotone" className="text-destructive shrink-0" />;
}

export default function SystemStatus() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/system-status");
      setStatus(r.data);
    } catch {
      setStatus({ _error: true });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Memoize the visible integrations list — must be BEFORE early returns (Rules of Hooks)
  const visibleKeys = useMemo(
    () => status ? ORDER.filter((k) => status[k]) : [],
    [status]
  );

  if (!status) return <div className="text-muted-foreground text-sm py-8">Chargement du diagnostic…</div>;
  if (status._error) return <div className="text-destructive text-sm py-8">Erreur lors du chargement — vérifiez vos droits admin.</div>;

  const overall = status._overall_ok;
  const checkedAt = status._checked_at ? new Date(status._checked_at).toLocaleString("fr-FR") : "—";

  return (
    <div data-testid="admin-system-status">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="label-uppercase mb-1">Santé des intégrations</div>
          <h2 className="font-display text-2xl font-medium">
            {overall ? "Tous les systèmes sont opérationnels" : "Attention — une ou plusieurs intégrations sont en panne"}
          </h2>
          <div className="text-xs text-muted-foreground mt-1">Dernière vérification : {checkedAt}</div>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="inline-flex items-center gap-1.5 border border-border hover:border-foreground px-3 py-2 text-sm rounded-sm disabled:opacity-50"
          data-testid="system-status-refresh"
        >
          <ArrowClockwise size={14} className={busy ? "animate-spin" : ""} />
          {busy ? "Vérification…" : "Rafraîchir"}
        </button>
      </div>

      <div className={`rounded-sm p-4 mb-6 border ${overall ? "bg-primary/10 border-primary/30 text-primary" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
        <div className="flex items-center gap-2 font-semibold">
          {overall ? <CheckCircle size={18} weight="fill"/> : <XCircle size={18} weight="fill"/>}
          {overall ? "Statut global : OK" : "Statut global : KO"}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleKeys.map((k) => {
          const s = status[k];
          const label = LABELS[k] || { title: k, desc: "" };
          return (
            <div key={k} className="bg-card border border-border rounded-sm p-5" data-testid={`system-status-card-${k}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-display text-lg font-medium">{label.title}</div>
                  <div className="text-xs text-muted-foreground leading-snug">{label.desc}</div>
                </div>
                <Dot ok={s.ok} configured={s.configured} />
              </div>
              <div className={`text-sm mt-3 ${s.ok ? "text-foreground" : "text-destructive"}`}>
                {s.detail}
              </div>
              {s.meta && (
                <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground font-mono break-all">
                  {Object.entries(s.meta).map(([mk, mv]) => (
                    <div key={mk} className="flex gap-2">
                      <span className="opacity-70">{mk}:</span>
                      <span className="flex-1">{
                        typeof mv === "object"
                          ? `${Array.isArray(mv) ? mv.length : Object.keys(mv || {}).length} éléments`
                          : String(mv ?? "—")
                      }</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
